import { Injectable } from '@angular/core';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { environment } from '../../environments/environment';
import { map, Observable, of, switchMap, catchError, finalize, shareReplay, tap } from 'rxjs';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { NavController } from '@ionic/angular';
import { AIMode, IUser } from '../models/user.interface';
import { IAccount } from '../models/account.interface';
import { IDashboard } from '../models/dashboard.interface';
import { AccountService } from './account.service';
import { ErrorService } from './error.service';
import { IMetricAggregated } from '../models/metric-aggregated.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private accountService: AccountService,
    private navCtrl: NavController,
    private errorService: ErrorService,
  ) { }

  /**
   * Switch to the given account and navigate to the specified route.
   * Delegates to selectActiveAccount, which persists the selection server-side and
   * re-fetches accounts. Navigation happens only on success. On error the failure is
   * surfaced via ErrorService and no navigation occurs — the user stays put.
   * @param account The account to switch to.
   * @param route The Ionic root route to navigate to on success.
   * @returns Observable<void> — callers must subscribe; navigation is inside the pipe.
   */
  switchAccountAndNavigate(account: IAccount, route: string): Observable<void> {
    return this.selectActiveAccount(account).pipe(
      tap(() => { this.navCtrl.navigateRoot(route); }),
      catchError((err: unknown) => {
        const message = (err instanceof Error ? err.message : null) ?? 'Failed to switch account.';
        this.errorService.showError(message);
        return of(undefined);
      })
    );
  }

  /**
   * Select an active account for the current user.
   * This is a user-level preference (which account the user is currently using).
   * This now returns an Observable to be more consistent with RxJS patterns.
   */
  selectActiveAccount(account: any): Observable<void> {
    if (!account || !account._id) {
      try { this.accountService.setActiveAccountLocal(null); } catch (_) {}
      return of(undefined);
    }

    // Deduplicate in-flight selects for the same account id.
    const requestedId = account._id;
    const acs: any = this.accountService as any;
    acs.__selectInFlight = acs.__selectInFlight || {};
    if (acs.__selectInFlight[requestedId]) { return acs.__selectInFlight[requestedId]; }

    // Optimistically set local active account so UI updates immediately
    try { this.accountService.setActiveAccountLocal(account); } catch (_) {}

    // Persist the selected active account as a user property
    const url = `${this.apiUrl}/accounts/${requestedId}/select`;

    const req$ = this.http.post(url, {}).pipe(
      switchMap(() => {
        // After the API call is successful, we can be sure the selection is persisted.
        // We can then fetch the user's accounts again to ensure the active account is correctly reflected.
        return this.accountService.getAccounts(true);
      }),
      map((): void => undefined),
      catchError((err) => {
        console.error('Failed to select active account', err);
        return of(undefined);
      }),
      finalize(() => {
        delete acs.__selectInFlight[requestedId];
      }),
      shareReplay(1)
    );
    
    acs.__selectInFlight[requestedId] = req$;
    return req$;
  }

  /**
   * Register user
   * @param userData user data
   * @returns 
   */
  registerUser(userData: any): Promise<HttpResponse> {
    const url = `${this.apiUrl}/users/register`;

    const options = {
      url: url,
      data: userData,
    };

    return CapacitorHttp.post(options);
  }


  /**
   * Get user
   * @param userId user id
   */
  getUser(userId: string): Observable<IUser> {
    const url = `${this.apiUrl}/users/${userId}`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    // Backend may now return { user, accounts, activeAccount } to reduce round trips.
    // Map that back to the IUser object so existing callers continue to work.
    return this.http.get<any>(url, options).pipe(
      map((resp: any) => {
        if (!resp) return null as any;
        // If server returned wrapper object, extract user
        if (resp.user) return resp.user as IUser;
        // Otherwise assume full user was returned directly
        return resp as IUser;
      })
    );
  }

  /**
   * Get raw user + accounts wrapper (used by AuthService to hydrate accounts cache)
   */
  getUserWithAccounts(userId: string): Observable<any> {
    const url = userId ? `${this.apiUrl}/users/${userId}` : `${this.apiUrl}/users/me`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.http.get<any>(url, options);
  }

  /**
   * Get the authenticated user + accounts (convenience wrapper for /users/me)
   */
  getMe(): Observable<any> {
    const url = `${this.apiUrl}/users/me`;
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.http.get<any>(url, options);
  }

  /**
   * Get metrics for the authenticated user.
   */
  getMeMetrics(): Observable<IMetricAggregated[]> {
    const url = `${this.apiUrl}/users/me/metrics`;
    return this.http.get<IMetricAggregated[]>(url);
  }


  /**
  * Upload user logo
  * @param userId ID of the user
  * @param file Logo file to upload
  * @returns Observable of the upload progress or response
  */
  uploadUserLogo(userId: string, file: File): Observable<HttpEvent<any>> {
    const url = `${this.apiUrl}/users/${userId}/logo`;

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('logo', file);

    // Use HttpClient with multipart/form-data headers
    return this.http.post<any>(url, formData, {
      reportProgress: true,
      observe: 'events', // Observing events to track upload progress
    });
  }


  /**
   * Update user details
   * Sends only address, apt, state, zipCode, and city
   * @param userId ID of the user
   * @param details Object containing address, apt, state, zipCode, and city
   * @returns Observable of the updated user
   */
  updateUserDetails(userId: string, details: { address?: string; apt?: string; state?: string; zipCode?: string; city?: string; }): Observable<any> {
    const url = `${this.apiUrl}/users/${userId}/details`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    // Send only the specified fields in the request body
    const { address, apt, state, zipCode, city } = details;

    return this.http.post<any>(url, { address, apt, state, zipCode, city }, options);
  }


  /**
   * Update AI mode
   * @param userId user id
   * @param aiMode AI mode to set
   * @returns Observable of the updated user
   */
  updateAIMode(userId: string, aiMode: AIMode) {
    const url = `${this.apiUrl}/users/${userId}/ai-mode`;
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

    return this.http.post(url, { aiMode }, options);
  }

  /**
   * Update user's preferred theme
   * @param userId ID of the user
   * @param preferredTheme 'system' | 'light' | 'dark'
   */
  updatePreferredTheme(userId: string, preferredTheme: 'system' | 'light' | 'dark'): Observable<{
    success: boolean;
    message: string;
    preferredTheme?: 'system' | 'light' | 'dark';
  }> {
    const url = `${this.apiUrl}/users/${userId}/preferred-theme`;
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.http.post<{
      success: boolean;
      message: string;
      preferredTheme?: 'system' | 'light' | 'dark';
    }>(url, { preferredTheme }, options);
  }



  /**
 * Update the firstTime property of a user
 * @param userId user id
 * @param firstTime value to set for the firstTime property
 * @returns Observable of the updated user
 */
  updateFirstTime(userId: string, firstTime: boolean): Observable<any> {
    const url = `${this.apiUrl}/users/${userId}/first-time`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    return this.http.post(url, { firstTime }, options);
  }


  /**
 * Link an account of a user
 * @param userId user id
 * @param linkedUserId id of user to link
 * @returns Observable of the updated user
 */
  linkAccount(userId: string, linkedUserId: string): Observable<any> {
    throw new Error('linkAccount is deprecated; accounts are managed via the Accounts feature.');
  }



  /**
* Unlink an account of a user
* @param userId user id
* @param linkedUserId id of user to link
* @returns Observable of the updated user
*/

  unlinkAccount(userId: string, linkedUserId: string): Observable<any> {
    throw new Error('unlinkAccount is deprecated; accounts are managed via the Accounts feature.');
  }


  /**
   * 
   * @param user 
   * @returns 
   */
  getUserLogoUrl(userId: string, logo: string): string {
    if (!logo) return this.getDefaultProfileImage();
    // Return a client-rooted path so the browser requests the file from the UI server (e.g., http://localhost:8100)
    // If the logo value is already a remote URL, callers should ensure it is passed through directly.
    if (logo.startsWith('http') || logo.startsWith('//')) return logo;
    return `/assets/users/${userId}/${logo}`;
  }

  /**
   * Returns the path to the default profile image, respecting the user's OS theme.
   * @returns The path to the light or dark mode default person icon.
   */
  getDefaultProfileImage(): string {
    const isDarkTheme = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme = isDarkTheme ? 'dark' : 'light';
    return `assets/icon/menu/${theme}/person.svg`;
  }


  /**
* Get user
* @param userId user id
*/
  getUserDashboard(userId: string): Observable<IDashboard> {
    const url = `${this.apiUrl}/users/${userId}/dashboard`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.get<IDashboard>(url, options);
  }

  /**
   * Request account cancellation for the given user.
   * @param userId The ID of the user to cancel.
   * @param immediate If true, cancel immediately. Otherwise, cancel at the end of the billing period.
   */
  cancelAccount(userId: string, immediate: boolean = false): Observable<any> {
    const url = `${this.apiUrl}/users/${userId}/cancel`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<any>(url, { immediate }, options);
  }

  /**
   * Reactivate a subscription that is pending cancellation.
   * @param userId The ID of the user to reactivate.
   */
  reactivateSubscription(userId: string): Observable<any> {
    const url = `${this.apiUrl}/users/${userId}/reactivate-subscription`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<any>(url, {}, options);
  }

}

